import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Code, TemplateSelection, NoteContent, Sentence } from '@ax/constants';
import {
  fromNoteContentToTemplateSelections,
  fromOrganizeSelectionsToHSFormValues,
} from './utilities.ts';
import {
  NoteContentLoadedPayload,
  NoteHSFormValueUpdatePayload,
  NoteState,
  UpdateNoteTemplateSucceededPayload,
  AddReevaluationEndedPayload,
  AddReevaluationStartedPayload,
  CreateReevaluationPayload,
} from './interfaces';
import { selectedVisitSlice } from '../selectedVisit';

export const noteInitialValue: NoteState = {
  id: '1',
  templates: [],
  hsForm: fromOrganizeSelectionsToHSFormValues([]),
  noteTemplateSelectedId: null,
  noteMarkdownContent: {
    content: '',
    selection: '',
  },
  noteSections: [],
  progressSentences: {
    addNewModalOpen: false,
    hasError: false,
    isAddingNewSentence: false,
    items: []
  },
  isSavingNewReeval: false,
};

export const noteSlice = createSlice({
  name: 'note',
  initialState: noteInitialValue,
  extraReducers: (builder) => {
    builder
      .addCase(selectedVisitSlice.actions.setSelectedVisitDetails, (state, action) => {
        if (!action.payload.uuid || state.id === action.payload.uuid) {
          return {
            ...state,
          };
        }

        return {
          ...noteInitialValue,
          id: action.payload.uuid,
        };
      })
      .addCase(selectedVisitSlice.actions.clearVisitDetails, () => {
        return {
          ...noteInitialValue,
        };
      });
  },
  reducers: {
    icd10CodeSelected(
      state,
      action: PayloadAction<{
        currentSelection?: Partial<Code>;
        newCode: Partial<Code>;
        noteId: string;
        templateId: number;
        templateName: string;
        section: string;
      }>,
    ) {
      return {
        ...state,
        templates: state.templates.map((template: TemplateSelection) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: true };
          }
          return template;
        }),
      };
    },
    updateNoteTemplateSucceeded: (
      state,
      action: PayloadAction<UpdateNoteTemplateSucceededPayload>,
    ) => {
      if (action.payload.noteId !== state.id) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        noteTemplateSelectedId: action.payload.templateId,
      };
    },
    noICD10CodeCreatedSucceeded(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    icd10DropdownOpened(state, _: PayloadAction<any>) {
      return {
        ...state,
      };
    },
    noICD10CodeCreatedFailed(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    icd10CodeReplaceSucceeded(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    icd10CodeReplaceFailed(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    icd10CodeAcceptSucceeded(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    icd10CodeAcceptFailed(state, action: PayloadAction<any>) {
      return {
        ...state,
        templates: state.templates.map((template) => {
          if (
            template.section === action.payload.section &&
            template.complaintId === action.payload.templateId
          ) {
            return { ...template, loading: false };
          }
          return template;
        }),
      };
    },
    noteContentLoaded(state, action: PayloadAction<NoteContentLoadedPayload>) {
      const noteSections: string[] =
        Array.from(
          new Set(action.payload?.noteContent?.getNoteContent.map((i: NoteContent) => i.Section)),
        ) ?? [];
      let noteMarkdownContent = {
        content: '',
        selection: '',
      };

      const wholeNoteItems: NoteContent[] = action.payload.noteContent?.getNoteContent.filter(
        (item: NoteContent) => item.Section === 'WHOLE_NOTE',
      );
      const latestWholeNoteItem: NoteContent = wholeNoteItems.sort(
        (a, b) => new Date(b.UpdatedAt).getTime() - new Date(a.UpdatedAt).getTime(),
      )[0];

      if (latestWholeNoteItem) {
        noteMarkdownContent = {
          content: latestWholeNoteItem.Content,
          selection: latestWholeNoteItem.selection,
        };
      }

      return {
        ...state,
        id: action.payload.noteId,
        templates: fromNoteContentToTemplateSelections(action.payload.noteContent),
        noteTemplateSelectedId: action.payload.noteContent?.getNote?.templateIdSelected || null,
        progressSentences: {
          ...state.progressSentences,
          items: action.payload.noteContent?.getNoteContent.filter((item: NoteContent) => item.Section === 'PROGRESS').map(item => ({...item, loading: false})) || []
        },
        hsForm: fromOrganizeSelectionsToHSFormValues(
          action.payload.noteContent?.getNote?.organizeSelections?.items,
          state.hsForm,
        ),
        noteSections,
        noteMarkdownContent,
      };
    },
    noteHSFormValueUpdateRequested(state, action: PayloadAction<NoteHSFormValueUpdatePayload>) {
      if (state.hsForm[action.payload.valueKey].value === action.payload.value) {
        return;
      }

      state.hsForm[action.payload.valueKey].isLoading = true;
      state.hsForm[action.payload.valueKey].value = action.payload.value;
      return state;
    },
    noteHSFormValueUpdateFailed(state, action: PayloadAction<NoteHSFormValueUpdatePayload>) {
      state.hsForm[action.payload.valueKey].isLoading = false;
      state.hsForm[action.payload.valueKey].error = true;
      return state;
    },
    noteHSFormValueUpdated(state, action: PayloadAction<NoteHSFormValueUpdatePayload>) {
      if (state.id !== action.payload.noteId) {
        return state;
      }

      return {
        ...state,
        hsForm: {
          ...state.hsForm,
          [action.payload.valueKey]: {
            value: action.payload.value,
            identifier: action.payload.identifier,
            isLoading: false,
            error: false,
          },
        },
      };
    },
    noteHSFormInvalidated(state, _: PayloadAction<{ noteId: string }>) {
      return {
        ...state,
        hsForm: {
          ...state.hsForm,
          showErrors: true,
          showRequiredFieldsDialog: true,
        },
      };
    },
    noteHSFormRequiredFieldsDialogOpened(state, _: PayloadAction<{ noteId: string }>) {
      return { ...state };
    },
    addReevaluationStarted(state, _: PayloadAction<AddReevaluationStartedPayload>) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          addNewModalOpen: true,
        }
      };
    },
    addReevaluationEnded(state, _: PayloadAction<AddReevaluationEndedPayload>) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          addNewModalOpen: false,
          hasError: false,
        }
      };
    },
    createReevaluationRequested(state, _: PayloadAction<CreateReevaluationPayload>) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          isAddingNewSentence: true,
          hasError: false,
        }
      };
    },
    createReevaluationSucceeded(state, _: PayloadAction<CreateReevaluationPayload>) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          isAddingNewSentence: false,
          hasError: false,
        }
      };
    },
    createReevaluationFailed(state, _: PayloadAction<CreateReevaluationPayload>) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          isAddingNewSentence: false,
          hasError: true,
        }
      };
    },
    reevalSentenceUpdateRequested(
      state, action: PayloadAction<{ selection: string, noteId: string; content: string; sentence: Sentence }>,
    ) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          items: state?.progressSentences.items.map((sentence) => {
            if (sentence.selection === action.payload.sentence.selection) {
              return {...sentence, isLoading: true};
            }
            return sentence;
          }) || [],
        }
      };
    },
    reevalSentenceUpdateSucceeded(
      state,
      action: PayloadAction<{ selection: string, noteId: string; content: string; sentence: Sentence }>,
    ) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          items: state?.progressSentences.items.map((sentence) => {
            if (sentence.selection === action.payload.sentence.selection) {
              return {...sentence, isLoading: false};
            }
            return sentence;
          }) || [],
        }
      };
    },
    reevalSentenceUpdateFailed(
      state,
      action: PayloadAction<{ selection: string, noteId: string; content: string; sentence: Sentence }>,
    ) {
      return {
        ...state,
        progressSentences: {
          ...state.progressSentences,
          items: state?.progressSentences.items.map((sentence) => {
            if (sentence.selection === action.payload.sentence.selection) {
              return {...sentence, isLoading: false};
            }
            return sentence;
          }) || [],
        }
      };
    },
    noteHSFormRequiredFieldsDialogDismissed(state, _: PayloadAction<{ noteId: string }>) {
      return {
        ...state,
        hsForm: {
          ...state.hsForm,
          showErrors: true,
          showRequiredFieldsDialog: false,
        },
      };
    },
  },
});

export const {
  noteContentLoaded,
  icd10CodeAcceptSucceeded,
  icd10CodeAcceptFailed,
  icd10CodeSelected,
  icd10DropdownOpened,
  noICD10CodeCreatedFailed,
  noICD10CodeCreatedSucceeded,
  icd10CodeReplaceSucceeded,
  icd10CodeReplaceFailed,
  addReevaluationStarted,
  addReevaluationEnded,
  createReevaluationRequested,
  reevalSentenceUpdateRequested,
  noteHSFormInvalidated,
  noteHSFormRequiredFieldsDialogOpened,
  noteHSFormRequiredFieldsDialogDismissed,
  noteHSFormValueUpdateRequested,
  noteHSFormValueUpdated,
} = noteSlice.actions;

export default noteSlice.reducer;
